import { useState, useEffect } from 'react';
import { Pencil } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { EL_ID } from '../../lib/fuehrungsDiagramm';

export const SELECT_CLASS = 'w-full h-9 rounded-md border border-input bg-background px-3 text-sm';

// Bezeichnung, Funkrufname und Einsatzabschnitt eines Elements – gilt nur in diesem Szenario
export const ElementEditModal = ({ open, onOpenChange, element, abschnitte = [], onSave }) => {
  const [customName, setCustomName] = useState('');
  const [callsign, setCallsign] = useState('');
  const [abschnittId, setAbschnittId] = useState('');

  const isVehicle = element?.type === 'vehicle';
  const originalName = element?.properties?.name || '';

  useEffect(() => {
    if (open && element) {
      setCustomName(element.properties?.customName || '');
      setCallsign(element.properties?.callsign || '');
      setAbschnittId(element.properties?.abschnittId || '');
    }
  }, [open, element]);

  const handleSave = () => {
    onSave({
      customName: customName.trim(),
      callsign: callsign.trim(),
      ...(isVehicle ? { abschnittId } : {}),
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="element-edit-modal">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-wider flex items-center gap-2 text-lunefire-charcoal dark:text-white">
            <Pencil className="w-5 h-5 text-lunefire-red" />
            {originalName || 'Element'} bearbeiten
          </DialogTitle>
          <DialogDescription className="font-body">
            Gilt nur in diesem Szenario – die allgemeine Fahrzeugliste bleibt unverändert.
          </DialogDescription>
        </DialogHeader>

        <form
          className="space-y-4 py-2"
          onSubmit={(e) => { e.preventDefault(); handleSave(); }}
        >
          <div className="space-y-2">
            <Label htmlFor="element-name">Bezeichnung</Label>
            <Input
              id="element-name"
              value={customName}
              onChange={(e) => setCustomName(e.target.value)}
              placeholder={originalName}
              maxLength={60}
              data-testid="element-name-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="element-callsign">Funkrufname</Label>
            <Input
              id="element-callsign"
              value={callsign}
              onChange={(e) => setCallsign(e.target.value)}
              placeholder="z.B. Florian Lüneburg 1-46-1"
              maxLength={40}
              data-testid="element-callsign-input"
            />
          </div>

          {isVehicle && (
            <div className="space-y-2">
              <Label htmlFor="element-abschnitt">Unterstellt</Label>
              <select
                id="element-abschnitt"
                value={abschnittId}
                onChange={(e) => setAbschnittId(e.target.value)}
                className={SELECT_CLASS}
                data-testid="element-abschnitt-select"
              >
                <option value="">– nicht zugeordnet –</option>
                <option value={EL_ID}>Direkt der Einsatzleitung</option>
                {abschnitte.map(a => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground">
                Einsatzabschnitte legst du unter „Führungsstruktur“ an.
              </p>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Abbrechen
            </Button>
            <Button
              type="submit"
              className="bg-lunefire-red hover:bg-lunefire-red/90 text-white font-display uppercase tracking-wider"
              data-testid="element-save-btn"
            >
              Übernehmen
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ElementEditModal;
